import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Autosave from '@ckeditor/ckeditor5-autosave/src/autosave';
import BoldEditing from '@ckeditor/ckeditor5-basic-styles/src/bold/boldediting';
import CodeEditing from '@ckeditor/ckeditor5-basic-styles/src/code/codeediting';
import ItalicEditing from '@ckeditor/ckeditor5-basic-styles/src/italic/italicediting';
import StrikethroughEditing from '@ckeditor/ckeditor5-basic-styles/src/strikethrough/strikethroughediting';
import UnderlineEditing from '@ckeditor/ckeditor5-basic-styles/src/underline/underlineediting';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import Clipboard from '@ckeditor/ckeditor5-clipboard/src/clipboard';
import ClipboardPipeline from '@ckeditor/ckeditor5-clipboard/src/clipboardpipeline';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import CodeBlock from '@ckeditor/ckeditor5-code-block/src/codeblock';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Enter from '@ckeditor/ckeditor5-enter/src/enter';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import ExportPdf from '@ckeditor/ckeditor5-export-pdf/src/exportpdf';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import Image from '@ckeditor/ckeditor5-image/src/imageblock';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import TodoList from '@ckeditor/ckeditor5-list/src/todolist';
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import Typing from '@ckeditor/ckeditor5-typing/src/typing';
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
import { sessionURL } from 'logrocket';
import { DocumentsAPI } from 'api/document/document-core';
import { cloudServices } from 'config/ck-editor';
import { API_HOST } from 'config/environment';
import { EditorModes } from 'data/documents';
import { ApiKey, logger } from 'services';
import { typecastError } from 'utils/error';
import { unreachable } from 'utils/unreachable';
import { AIFill } from 'vendor/ckeditor/plugins/ai-fill/plugin';
import { Comment } from 'vendor/ckeditor/plugins/comment';
import { Emoji } from 'vendor/ckeditor/plugins/emoji';
import { FreezeDocument } from 'vendor/ckeditor/plugins/freeze-document';
import { InfoPanel } from 'vendor/ckeditor/plugins/info-panel';
import { Mention } from 'vendor/ckeditor/plugins/mention/mention';
import { MentionCustomization } from 'vendor/ckeditor/plugins/mention/mention-customization';
import { Task } from 'vendor/ckeditor/plugins/task';
import { WidgetDropHandler } from 'vendor/ckeditor/plugins/widget-drop-handler';
import { emojiRenderer, infoPanelRenderer } from '../parts';

export const CKEditorPluginArray = [
  Alignment,
  Autoformat,
  Autosave,
  BlockQuote,
  BoldEditing,
  BoldEditing,
  Clipboard,
  ClipboardPipeline,
  CloudServices,
  CodeBlock,
  CodeEditing,
  Comment,
  EasyImage,
  Emoji,
  Enter,
  Essentials,
  ExportPdf,
  FontFamily,
  FontBackgroundColor,
  FontColor,
  FreezeDocument,
  FontSize,
  Heading,
  HorizontalLine,
  Image,
  ImageResize,
  ImageUpload,
  InfoPanel,
  ItalicEditing,
  Link,
  List,
  Mention,
  MentionCustomization,
  Paragraph,
  PageBreak,
  PasteFromOffice,
  RemoveFormat,
  StrikethroughEditing,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  TableColumnResize,
  Task,
  TodoList,
  Typing,
  UnderlineEditing,
  WidgetDropHandler,
  SimpleUploadAdapter,
  AIFill,
];

const CKEditorTableConfig = {
  contentToolbar: [
    'tableColumn',
    'tableRow',
    'mergeTableCells',
    'tableProperties',
    'tableCellProperties',
  ],
};

export const CKContextConfig = {
  cloudServices,
  collaboration: {
    channelId: 'context-id-rocketlane',
  },
};

const CKEditorConfig = {
  plugins: CKEditorPluginArray,
  table: CKEditorTableConfig,
  emoji: {
    renderer: emojiRenderer,
  },
  infopanel: {
    iconRenderer: infoPanelRenderer,
  },
  resizeOptions: [
    {
      name: 'imageResize:original',
      value: null,
      label: 'Original',
    },
    {
      name: 'imageResize:50',
      value: '50',
      label: '50%',
    },
    {
      name: 'imageResize:75',
      value: '75',
      label: '75%',
    },
  ],
  link: {
    defaultProtocol: 'http://',
    addTargetToExternalLinks: true,
  },
  mention: {
    feeds: [
      {
        marker: '@',
      },
    ],
  },
  variables: {
    feeds: [
      {
        marker: '{',
      },
    ],
  },
  aiFillSelector: {
    feeds: [
      {
        marker: '/',
      },
    ],
  },
  autosave: {
    waitingTime: 2000,
    save: async (editor) => {
      if (!editor.readyToAutoSave) {
        return Promise.resolve();
      }
      try {
        const { sectionid, projectid, docid } = editor.sourceElement?.dataset;
        const versionId = editor.plugins?.get(
          'RealTimeCollaborationClient',
        ).cloudDocumentVersion;

        if (Number(sectionid) > 0) {
          return await DocumentsAPI.updateDocument(
            projectid,
            docid,
            sectionid,
            versionId,
            editor.getData(),
          );
        }
      } catch (err) {
        const error = typecastError(err);

        logger.error('Error in CKE autosave', {
          error,
          content: editor?.getData?.() || 'No data',
          logrocketUrl: sessionURL ?? 'No session available',
        });
        logger.log('Error in CKE autosave', {
          content: editor?.getData?.() || 'No data',
          logrocketUrl: sessionURL ?? 'No session available',
          error,
        });

        return Promise.resolve();
      }

      return Promise.resolve();

      // TODO: show saving indicator
    },
  },
  simpleUpload: {
    uploadUrl: `${API_HOST}/api/v1/attachments/cke-upload`,
    withCredentials: false, // not to confuse with api key. this is for https cors credentials
  },
};

export const getCkEditorUploadConfig = () => {
  const key = ApiKey.get();

  if (!key) {
    return unreachable(key as never);
  }

  return {
    simpleUpload: {
      ...CKEditorConfig.simpleUpload,
      headers: {
        'api-key': key[1],
      },
    },
  };
};

export const getCkEditorConfig = () => {
  return {
    ...CKEditorConfig,
    ...getCkEditorUploadConfig(),
  };
};

export const getCkeRevisionEditorConfig = (editorMode: EditorModes) => {
  return editorMode === EditorModes.Private
    ? {
        revisionHistory: {
          editorContainer: document.querySelector(`#${editorMode}`),
          viewerContainer: document.querySelector(
            '#revision-viewer-container-private',
          ),
          viewerEditorElement: document.querySelector(
            '#revision-viewer-editor-private',
          ),
          viewerSidebarContainer: document.querySelector(
            '#revision-viewer-sidebar-private',
          ),
        },
      }
    : {
        revisionHistory: {
          editorContainer: document.querySelector(`#${editorMode}`),
          viewerContainer: document.querySelector('#revision-viewer-container'),
          viewerEditorElement: document.querySelector(
            '#revision-viewer-editor',
          ),
          viewerSidebarContainer: document.querySelector(
            '#revision-viewer-sidebar',
          ),
        },
      };
};
